/**
 * bleUtils.js  — BleManager 공통 유틸리티
 *
 * BleManager.js 리팩토링에서 추출한 공통 로직:
 *   - authHeaders()        : Authorization 헤더 생성
 *   - isExpired()          : 토큰 만료 여부 확인
 *   - useClipboard()       : 복사 + 2초 피드백 훅
 *   - useApiCall()         : fetch 래퍼 (loading / error 상태 통합)
 *   - BottomSheet          : 재사용 가능한 바텀시트 레이아웃
 *   - StatusBadge          : 활성/비활성/만료 뱃지
 *   - TokenDateRange       : 시작일~만료일 표시 줄
 *   - DoorLabels           : door_ids → 레이블 변환
 */

import React, { useState, useCallback } from "react";

const API_BASE = "https://adria.terrier-ule.ts.net";

// ── 인증 헤더 ────────────────────────────────────────────────
export const authHeaders = (token) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

// ── 만료 판정 ────────────────────────────────────────────────
export const isExpired = (expiresAt) => new Date(expiresAt) < new Date();

// ── 클립보드 복사 훅 (2초 피드백) ───────────────────────────
export function useClipboard() {
  const [copiedKey, setCopiedKey] = useState(null);

  const copy = useCallback((key, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  }, []);

  return { copiedKey, copy };
}

// ── 범용 fetch 래퍼 ─────────────────────────────────────────
export function useApiCall() {
  const [loading, setLoading] = useState(false);

  const call = useCallback(async (fn) => {
    setLoading(true);
    try {
      return await fn();
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, call };
}

// ── 공통 스타일 상수 ─────────────────────────────────────────
export const S = {
  btn:     (bg) => ({ padding: "8px 13px", backgroundColor: bg, color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }),
  btnFull: (bg) => ({ width: "100%", padding: "12px", backgroundColor: bg, color: "#fff", border: "none", borderRadius: "10px", fontWeight: "bold", fontSize: "14px", cursor: "pointer", marginTop: "4px" }),
  badge:   (c, bg) => ({ fontSize: "10px", fontWeight: "bold", color: c, backgroundColor: bg, padding: "2px 7px", borderRadius: "20px", display: "inline-block" }),
  empty:   { textAlign: "center", padding: "40px", color: "#9ca3af", fontSize: "13px" },
  label:   { fontSize: "12px", color: "#6b7280", marginBottom: "4px", display: "block" },
  input:   { width: "100%", padding: "10px", borderRadius: "8px", boxSizing: "border-box", border: "1px solid #d1d5db", fontSize: "13px", marginBottom: "10px" },
  row:     { display: "flex", gap: "8px" },
  card:    (bc) => ({ backgroundColor: "#fff", padding: "14px", borderRadius: "12px", marginBottom: "10px", borderLeft: "6px solid " + bc, boxShadow: "0 2px 6px rgba(0,0,0,0.06)" }),
  tabBtn:  (on) => ({ flex: 1, padding: "10px", border: "none", borderRadius: "10px", fontWeight: "bold", fontSize: "12px", cursor: "pointer", backgroundColor: on ? "#4f46e5" : "#fff", color: on ? "#fff" : "#374151", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }),
};

// ── 바텀시트 레이아웃 ────────────────────────────────────────
export function BottomSheet({ onClose, title, children }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1000,
               display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={onClose}
    >
      <div
        style={{ backgroundColor: "#fff", borderRadius: "20px 20px 0 0", padding: "24px 20px 32px",
                 width: "100%", maxWidth: "500px", boxSizing: "border-box", maxHeight: "85vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", fontSize: "16px" }}>{title}</div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#9ca3af" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── 상태 뱃지 ────────────────────────────────────────────────
export function StatusBadge({ isActive, expiresAt }) {
  const expired = expiresAt ? isExpired(expiresAt) : false;
  if (expired)      return <span style={S.badge("#6b7280", "#f3f4f6")}>만료</span>;
  if (isActive)     return <span style={S.badge("#065f46", "#d1fae5")}>● 활성</span>;
  return                   <span style={S.badge("#6b7280", "#f3f4f6")}>○ 비활성</span>;
}

// ── 날짜 범위 표시 ───────────────────────────────────────────
export function TokenDateRange({ startsAt, expiresAt }) {
  return (
    <span style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "6px" }}>
      {startsAt ? startsAt.slice(0, 10) + " ~ " : "~ "}{expiresAt.slice(0, 10)}
    </span>
  );
}

// ── door_ids → 레이블 변환 ───────────────────────────────────
export function DoorLabels({ doorIds = [], L }) {
  const text = doorIds
    .map((id) => (id === "MAIN_DOOR" ? L.main_door : `${id}${L.room_suffix} ${L.unit_door}`))
    .join(" + ");
  return (
    <span style={{ fontSize: "11px", color: "#6366f1", marginLeft: "6px" }}>
      {text}
    </span>
  );
}

// ── 메시지 박스 (성공/에러) ──────────────────────────────────
export function MsgBox({ msg }) {
  if (!msg) return null;
  const ok = msg.startsWith("✅");
  return (
    <div style={{ padding: "10px", borderRadius: "8px", marginBottom: "12px", fontSize: "13px", fontWeight: "bold",
                  backgroundColor: ok ? "#f0fdf4" : "#fef2f2",
                  color:           ok ? "#15803d" : "#dc2626" }}>
      {msg}
    </div>
  );
}

// ── 환영 알림톡 발송 공통 함수 ───────────────────────────────
export async function sendWelcomeKakao({ token, phone, doorToken, roomNo, userName, welcomeMsg, templateId }) {
  const res = await fetch(`${API_BASE}/api/ble/send-welcome`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      phone,
      door_token:  doorToken,
      room_no:     roomNo,
      user_name:   userName,
      welcome_msg: welcomeMsg || "",
      template_id: templateId || "welcome",
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || JSON.stringify(data));
  return data;
}

// ── 토큰 CRUD API 함수들 ─────────────────────────────────────
export const tokenApi = {
  list: (token, roomNo) =>
    fetch(`${API_BASE}/api/ble/tokens${roomNo ? `?room_no=${roomNo}` : ""}`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : []),

  create: (token, body) =>
    fetch(`${API_BASE}/api/ble/tokens`, {
      method: "POST", headers: authHeaders(token), body: JSON.stringify(body),
    }),

  patch: (token, id, body) =>
    fetch(`${API_BASE}/api/ble/tokens/${id}`, {
      method: "PATCH", headers: authHeaders(token), body: JSON.stringify(body),
    }),

  delete: (token, id) =>
    fetch(`${API_BASE}/api/ble/tokens/${id}`, { method: "DELETE", headers: authHeaders(token) }),

  toggle: async (token, id, currentActive) =>
    fetch(`${API_BASE}/api/ble/tokens/${id}`, {
      method: "PATCH", headers: authHeaders(token),
      body: JSON.stringify({ is_active: !currentActive }),
    }),
};

// ── 공통 도어 옵션 빌드 ──────────────────────────────────────
export function buildDoorOptions(roomNo, L) {
  const cleanNo = String(roomNo || "").replace(L.room_suffix || "호", "");
  return [
    { id: "MAIN_DOOR", label: L.main_door },
    ...(roomNo ? [{ id: cleanNo, label: `${roomNo} ${L.unit_door}` }] : []),
  ];
}

export { API_BASE };
